import { dateSort, numberSort } from 'components/LrGrid';
import { formatBoolToYesNo, formatDecimal, formatDollar, formatModString, formatPercent } from 'helpers/formatters';
import { SubmissionDetails } from 'types/submission.type';

import React from 'react';
import { LocationOptionsData } from 'types/types.type';
import { sovModCellRenderer } from 'helpers/sov';
import SoftCostLineItemsInputForm from './Components/SoftCostLineItemsInputForm';
import { ColDef, ColGroupDef } from 'ag-grid-enterprise';
import { ICellRendererParams } from 'ag-grid-enterprise';

export const getSovColumnDefs = (
    submission: SubmissionDetails,
    isAgent: boolean,
    locationOptions: LocationOptionsData
): (ColDef | ColGroupDef)[] => [
    {
        headerName: 'Location Name',
        valueGetter: ({ data }: any) => {
            return data.markedAsDeleted ? `${data.locationName}*` : data.locationName;
        },
        pinned: 'left',
        lockPinned: true,
    },
    {
        headerName: 'Effective Date',
        comparator: dateSort,
        valueGetter: ({ data }: any) =>
            data.originalEffectiveDate || data.effectiveDate || submission.effectiveDate || '',
    },
    {
        headerName: 'Location Address',
        field: 'address1',
    },
    {
        headerName: 'Location Address 2',
        field: 'address2',
    },
    {
        headerName: 'City',
        field: 'city',
    },
    {
        headerName: 'State',
        field: 'state',
    },
    {
        headerName: 'Zip',
        field: 'zip',
    },
    {
        headerName: 'County',
        field: 'county',
    },
    {
        headerName: 'Latitude',
        field: 'locationLat',
    },
    {
        headerName: 'Longitude',
        field: 'locationLon',
    },
    {
        headerName: 'Year of Construction',
        field: 'yearOfConstruction',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Construction Type',
        valueGetter: ({ data }: any) => locationOptions.LOCATION_CONSTRUCTION_TYPES[data.constructionType],
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: '#Bldings',
        field: 'numberOfBuildings',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: '#Stories',
        field: 'numberOfStories',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Min Separation (ft)',
        field: 'minimumSpaceSeparationFeet',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Is Fenced',
        field: 'isFencedType',
        valueGetter: ({ data }: any) => locationOptions.LOCATION_IS_FENCED_TYPES[data.isFencedType] || '',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Fence Height',
        field: 'fenceHeightType',
        valueGetter: ({ data }: any) => locationOptions.LOCATION_FENCE_HEIGHT_TYPES[data.fenceHeightType] || '',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Guard',
        field: 'isGuarded',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatBoolToYesNo(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Elec. Intrusion Det.',
        field: 'hasElectronicIntrusionDetection',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatBoolToYesNo(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Elec. Intrusion Det. Vendor',
        field: 'electronicIntrusionDetectionVendorName',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Elec. Intrusion Central Station',
        field: 'isElectronicIntrusionDetectionMonitored',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatBoolToYesNo(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Water Flow Det.',
        field: 'hasWaterFlowDetection',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatBoolToYesNo(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Water Flow Central Station',
        field: 'isWaterFlowCentralStationMonitored',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatBoolToYesNo(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Water Flow Auto Shutoff',
        field: 'hasWaterFlowAutoRemoteShutOff',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatBoolToYesNo(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Ext. Comb. Exposure < 100ft',
        field: 'hasExternalCombustibleExposuresLt100ft',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatBoolToYesNo(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Hard Costs',
        field: 'hardCosts',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Soft Costs',
        field: 'softCosts',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => (
            <>{sovModCellRenderer(data, (val: any) => formatDollar(val, 0))}</>
        ),

        aggFunc: 'aggValues',
    },
    {
        headerName: 'Soft Cost Line Items',
        field: 'locationSoftCosts',
        cellRenderer: (params: ICellRendererParams) => (
            <SoftCostLineItemsInputForm
                value={params.data.locationSoftCosts || []}
                parentData={{ softCosts: params.data.softCosts }}
                onChange={() => null}
                isReadOnly={true}
            />
        ),
        width: 180,
    },
    {
        headerName: 'Loss of Gross Earnings',
        field: 'lossOfGrossEarnings',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Loss of Rental Income',
        field: 'lossOfRentalIncome',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Total Contract Value',
        field: 'totalInsurableValue',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
        pinned: 'right',
        lockPinned: true,
    },
    {
        headerName: 'Removed',
        field: 'markedAsDeleted',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatBoolToYesNo(val)),
        aggFunc: 'aggValues',
    },
];
